import { useEffect, useId, useMemo, useState } from 'react';
import { MapPin, Search } from 'lucide-react';
import { searchLocations } from '@/lib/lamal';

const inputClass = 'h-12 w-full rounded-xl border border-border bg-white pl-11 pr-4 text-sm text-brand-deep outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20';

export function formatLocation(location) {
  if (!location) return '';
  return `${location.z} ${location.l}${location.n !== location.l ? ` · ${location.n}` : ''} (${location.c})`;
}

export function LamalLocationSearch({ locations, value, onChange, lang = 'it', required = true, label, compact = false }) {
  const listboxId = useId();
  const [query, setQuery] = useState(() => formatLocation(value));
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const matches = useMemo(() => searchLocations(locations, query, 100), [locations, query]);

  useEffect(() => {
    if (value) setQuery(formatLocation(value));
  }, [value]);

  const select = (location) => {
    onChange(location);
    setQuery(formatLocation(location));
    setOpen(false);
    setActive(0);
  };

  const handleKeyDown = (event) => {
    if (!open || matches.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => (current + 1) % matches.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => (current - 1 + matches.length) % matches.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      select(matches[active]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <label className="relative block">
      {label && <span className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-brand-deep/60">{label}</span>}
      <span className="relative block">
        <Search className="pointer-events-none absolute left-3.5 top-3.5 h-5 w-5 text-primary" />
        <input
          className={inputClass}
          value={query}
          placeholder={lang === 'it' ? 'Es. 6900 oppure Lugano' : 'E.g. 6900 or Lugano'}
          autoComplete="off"
          role="combobox"
          aria-expanded={open && matches.length > 0}
          aria-controls={listboxId}
          aria-autocomplete="list"
          required={required}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onKeyDown={handleKeyDown}
          onChange={(event) => {
            setQuery(event.target.value);
            onChange(null);
            setOpen(true);
            setActive(0);
          }}
        />
      </span>
      {open && query.trim().length >= 2 && (
        <div id={listboxId} role="listbox" className={`absolute z-40 mt-2 max-h-80 w-full overflow-y-auto rounded-xl border border-border bg-white shadow-xl ${compact ? 'text-left' : ''}`}>
          {matches.length > 0 ? matches.map((location, index) => (
            <button
              type="button"
              role="option"
              aria-selected={index === active}
              key={`${location.z}-${location.l}-${location.b}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => select(location)}
              className={`flex w-full items-start gap-3 border-b border-border/60 px-4 py-3 text-left text-sm last:border-0 ${index === active ? 'bg-primary/10' : 'hover:bg-brand-pale/60'}`}
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span><strong>{location.z} {location.l}</strong><span className="block text-xs text-brand-deep/55">{location.n} · {location.c}</span></span>
            </button>
          )) : <p className="px-4 py-3 text-sm text-brand-deep/55">{lang === 'it' ? 'Nessun comune o NPA trovato' : 'No municipality or postcode found'}</p>}
        </div>
      )}
    </label>
  );
}
